import * as React from 'react'
import { cn } from '@/registry/reapit/lib/utils'

// ─── lookup maps (static strings so Tailwind v4 scanner picks them all up) ────

const bgClasses = {
  background: 'bg-background',
  muted: 'bg-muted',
  card: 'bg-card',
  accent: 'bg-accent',
  primary: 'bg-primary',
  none: '',
} as const

const minHeightClasses = {
  screen: 'min-h-screen',
  full: 'min-h-full',
  auto: '',
} as const

const borderClasses = {
  none: '',
  bottom: 'border-b',
  top: 'border-t',
  both: 'border-t border-b',
  all: 'border',
} as const

const paddingXClasses = {
  none: '',
  sm: 'px-4',
  md: 'px-6',
  lg: 'px-8',
  xl: 'px-10',
} as const

const paddingYClasses = {
  none: '',
  sm: 'py-4',
  md: 'py-6',
  lg: 'py-8',
  xl: 'py-12',
} as const

// ─── Page ─────────────────────────────────────────────────────────────────────

export namespace Page {
  export type Background = keyof typeof bgClasses
  export type MinHeight = keyof typeof minHeightClasses

  export interface Props extends React.ComponentProps<'div'> {
    /** Render as a different element. */
    as?: React.ElementType
    /**
     * Page background colour.
     * 'background' → bg-background (default page colour)
     * 'muted'      → bg-muted (slightly off-white / grey)
     */
    background?: Background
    /**
     * Minimum height of the page.
     * 'screen' → min-h-screen (fills the viewport, default)
     * 'full'   → min-h-full
     * 'auto'   → no minimum
     */
    minHeight?: MinHeight
    /** Apply flex flex-col so children can fill remaining height. */
    flex?: boolean
  }
}

/**
 * Top-level page wrapper — typically the outermost element of a page block.
 * Applies `min-h-screen` and a background colour.
 *
 * ```tsx
 * <Page background="background">
 *   <TopBar />
 *   <main>...</main>
 * </Page>
 * ```
 */
export function Page({
  as: Comp = 'div',
  background = 'background',
  minHeight = 'screen',
  flex = false,
  className,
  children,
  ...props
}: Page.Props) {
  return (
    <Comp
      className={cn(minHeightClasses[minHeight], bgClasses[background], flex && 'flex flex-col', className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── Strip ────────────────────────────────────────────────────────────────────

export namespace Strip {
  export type Background = keyof typeof bgClasses
  export type Border = keyof typeof borderClasses
  export type Padding = keyof typeof paddingXClasses

  export interface Props extends React.ComponentProps<'div'> {
    /** Render as a different element (e.g. 'header', 'footer', 'section'). */
    as?: React.ElementType
    /**
     * Background colour.
     * 'muted'   → bg-muted  (most common — used for page header strips)
     * 'card'    → bg-card
     * 'accent'  → bg-accent
     */
    background?: Background
    /** Horizontal padding preset. Default: 'none'. */
    padding?: Strip.Padding
    /** Vertical padding preset. Default: 'none'. */
    paddingY?: Strip.Padding
    /** Border placement. Default: 'none'. */
    border?: Border
  }
}

/**
 * Full-width background band — for page headers, hero sections,
 * footer strips, or any element that spans the full viewport width
 * with a background colour.
 *
 * ```tsx
 * <Strip background="muted" border="bottom" padding="lg" paddingY="md">
 *   <Container maxWidth={1200}>...</Container>
 * </Strip>
 * ```
 */
export function Strip({
  as: Comp = 'div',
  background = 'none',
  padding = 'none',
  paddingY = 'none',
  border = 'none',
  className,
  children,
  ...props
}: Strip.Props) {
  return (
    <Comp
      className={cn(
        'w-full',
        bgClasses[background],
        paddingXClasses[padding],
        paddingYClasses[paddingY],
        borderClasses[border],
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export namespace Panel {
  export type Background = keyof typeof bgClasses
  export type Border = keyof typeof borderClasses
  export type Padding = keyof typeof paddingXClasses

  export interface Props extends React.ComponentProps<'div'> {
    /** Render as a different element. */
    as?: React.ElementType
    /**
     * Fixed width applied as an inline style.
     * Accepts a number (converted to px) or any CSS length string.
     * e.g. width={375} → style="width: 375px"
     */
    width?: number | string
    /**
     * Minimum width applied as an inline style.
     * Useful to set a lower bound when width is not fixed.
     */
    minWidth?: number | string
    /**
     * Whether the panel can shrink below its width in a flex container.
     * Default: false — panels hold their width.
     */
    shrink?: boolean
    /** Whether the panel should grow to fill available flex space. */
    grow?: boolean
    /** Optional background colour. */
    background?: Background
    /** Optional border. */
    border?: Border
    /** Horizontal padding preset. */
    padding?: Panel.Padding
    /** Vertical padding preset. */
    paddingY?: Panel.Padding
  }
}

/**
 * Fixed- or constrained-width flex child — for detail panels, sidebars,
 * or any element that needs to hold a specific width inside a flex row.
 *
 * ```tsx
 * <Flex gap={16}>
 *   <div className="flex-1 min-w-0">main content</div>
 *   <Panel width={375}>detail panel</Panel>
 * </Flex>
 * ```
 */
export function Panel({
  as: Comp = 'div',
  width,
  minWidth,
  shrink = false,
  grow = false,
  background = 'none',
  border = 'none',
  padding = 'none',
  paddingY = 'none',
  className,
  style,
  children,
  ...props
}: Panel.Props) {
  const inlineStyle: React.CSSProperties = {
    ...(width !== undefined ? { width: typeof width === 'number' ? `${width}px` : width } : {}),
    ...(minWidth !== undefined ? { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth } : {}),
    ...style,
  }

  return (
    <Comp
      className={cn(
        !shrink && 'shrink-0',
        grow && 'grow',
        bgClasses[background],
        borderClasses[border],
        paddingXClasses[padding],
        paddingYClasses[paddingY],
        className,
      )}
      style={Object.keys(inlineStyle).length ? inlineStyle : undefined}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── Well ─────────────────────────────────────────────────────────────────────

// ─── StatusDot ────────────────────────────────────────────────────────────────

const statusDotColourClasses = {
  success: 'bg-badge-success',
  danger: 'bg-badge-danger',
  warning: 'bg-badge-warning',
  pending: 'bg-badge-pending',
  neutral: 'bg-badge-neutral',
  'accent-1': 'bg-badge-accent-1',
  'accent-2': 'bg-badge-accent-2',
  'accent-3': 'bg-badge-accent-3',
} as const

export namespace StatusDot {
  export type Colour = keyof typeof statusDotColourClasses

  export interface Props {
    colour?: Colour
    className?: string
  }
}

/**
 * Small circular status indicator — 16×16px dot using badge semantic colour tokens.
 *
 * ```tsx
 * <StatusDot colour="accent-1" />
 * <StatusDot colour="success" />
 * ```
 */
export function StatusDot({ colour = 'neutral', className }: StatusDot.Props) {
  return (
    <span data-slot="status-dot" className={cn('size-4 rounded-full', statusDotColourClasses[colour], className)} />
  )
}

// ─── Well ─────────────────────────────────────────────────────────────────────

const wellPaddingClasses = {
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
  xl: 'p-6',
} as const

const wellRoundedClasses = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
} as const

const wellGapClasses = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  6: 'gap-6',
} as const

export namespace Well {
  export type Padding = keyof typeof wellPaddingClasses
  export type Rounded = keyof typeof wellRoundedClasses
  export type Gap = keyof typeof wellGapClasses

  export interface Props extends React.ComponentProps<'div'> {
    /** Render as a different element. */
    as?: React.ElementType
    /** Padding on all sides. Default: 'lg' → p-5. */
    padding?: Padding
    /** Border radius. Default: 'lg' → rounded-lg. */
    rounded?: Rounded
    /** Show a border. Default: true. */
    border?: boolean
    /**
     * Gap between direct children (applies flex-col + gap-*).
     * Omit if children don't need a stacking gap.
     */
    gap?: Gap
    /** Optional background. */
    background?: Strip.Background
  }
}

/**
 * Bordered inset container — for callouts, offer cards, and any content
 * that needs a visual grouping box with padding and a border.
 *
 * Replaces the raw pattern `border rounded-lg p-5`.
 *
 * ```tsx
 * <Well gap={2} className="mt-4">
 *   <Text size="sm" weight="semibold">10 Alpine Croft, Birmingham</Text>
 *   <TextMuted>£899,000</TextMuted>
 * </Well>
 * ```
 */
export function Well({
  as: Comp = 'div',
  padding = 'lg',
  rounded = 'lg',
  border = true,
  gap,
  background,
  className,
  children,
  ...props
}: Well.Props) {
  return (
    <Comp
      className={cn(
        wellPaddingClasses[padding],
        wellRoundedClasses[rounded],
        border && 'border',
        gap !== undefined && 'flex flex-col',
        gap !== undefined && wellGapClasses[gap],
        background && bgClasses[background],
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  )
}
